import * as borogove from "borogove";
import DOMPurify from "dompurify";
import { Button, Message } from "@chatscope/chat-ui-kit-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router";
import { ReactNode } from "react";
import {
	faCheck,
	faCheckDouble,
	faCircleExclamation,
	faClock,
	faPencil,
	faReply,
} from "@fortawesome/free-solid-svg-icons";

function shortDateTimeOnlyToday(date: Date) {
	const today =
		new Date().toLocaleString([], { dateStyle: "short" }) ===
		date.toLocaleString([], { dateStyle: "short" });
	return date.toLocaleString([], {
		dateStyle: today ? undefined : "short",
		timeStyle: "short",
	});
}

const renderReaction = (reaction: borogove.Reaction) => {
	return reaction.render<JSX.Element | string>(
		(text: string) => text,
		(alt: string, uri: string) => (
			<img crossOrigin="anonymous" alt={alt} src={uri} />
		),
	);
};

export const attachmentContent = (
	attachment: borogove.ChatAttachment,
	size?: string,
) => {
	if (attachment?.mime?.indexOf("image/") === 0) {
		return (
			<a href={attachment.uris[0]}>
				<img
					alt={attachment.name || "attachment"}
					crossOrigin="anonymous"
					src={attachment.uris[0]}
					style={{ height: size || "15em", objectFit: "scale-down" }}
				/>
			</a>
		);
	} else if (attachment?.mime?.indexOf("audio/") === 0) {
		return (
			<audio crossOrigin="anonymous" src={attachment.uris[0]} controls></audio>
		);
	} else if (attachment?.mime?.indexOf("video/") === 0) {
		return (
			<video
				crossOrigin="anonymous"
				src={attachment.uris[0]}
				style={{ height: size || "15em", objectFit: "scale-down" }}
				controls
			></video>
		);
	} else {
		return <a href={attachment.uris[0]}>attachment</a>;
	}
};

export const MessageBubble = ({
	chat,
	accountId,
	sender,
	parentSender,
	m,
	position,
	setSelectedThread,
	className,
	children,
}: {
	chat: borogove.Chat;
	accountId: string;
	sender: borogove.Participant;
	parentSender: borogove.Participant | null;
	m: borogove.ChatMessage;
	position: "first" | "last" | "single" | "normal";
	setSelectedThread: (id: string | null) => void;
	className?: string;
	children: ReactNode;
}) => {
	const navigate = useNavigate();

	const toggleReaction = (
		m: borogove.ChatMessage,
		reaction: borogove.Reaction,
	) => {
		const myReaction = (m.reactions.get(reaction.key) || []).find(
			(r: borogove.Reaction) => chat.getParticipantDetails(r.senderId).isSelf,
		);
		if (myReaction) {
			chat.removeReaction(m, myReaction);
		} else {
			chat.addReaction(m, reaction);
		}
	};

	const body = DOMPurify.sanitize(m.html(sender));
	const parentMessage = m.replyToMessage;
	const reactionEntries = [...(m.reactions.entries() || [])];
	const fullDateTimeHuman = new Date(m.timestamp)
		.toLocaleString([], {
			dateStyle: "full",
			timeStyle: "long",
		})
		.replace(/\s*\b[A-Z]{2,4}$/, ""); // remove trailing TZ abbrev

	return (
		<Message
			model={{ direction: m.isIncoming() ? "incoming" : "outgoing", position }}
			onClick={() => setSelectedThread(m.threadId)}
			id={`message:${m.serverId || m.localId}`}
			data-server-id={m.serverId}
			data-local-id={m.localId}
			className={
				(body.length < 25 && !m.replyToMessage ? "small" : "") +
				(m.type === borogove.MessageType.MessageChannelPrivate
					? "invite"
					: "") +
				" " +
				className
			}
		>
			{children}
			<Message.CustomContent>
				<div title={fullDateTimeHuman}>
					{parentMessage && (
						<>
							<Button
								title="Jump to parent"
								icon={<FontAwesomeIcon icon={faReply} />}
								onClick={() => navigate(`#message:${parentMessage.serverId}`)}
							>
								{parentSender?.displayName}
							</Button>
							<blockquote className="u-in-reply-to">
								<Message.HtmlContent
									html={DOMPurify.sanitize(m.replyToMessage.html())}
								/>
								{m.replyToMessage.attachments.map((attachment) =>
									attachmentContent(attachment),
								)}
							</blockquote>
						</>
					)}
					<Message.HtmlContent html={body} />
					{m.attachments.map((attachment) => attachmentContent(attachment))}
					<div className="meta">
						<div>
							{m.threadId && (
								<img
									src={m.threadIcon() ?? undefined}
									alt={"thread " + m.threadId.substring(0, 5)}
									className="thread"
								/>
							)}
							{m.timestamp && ["last", "single"].includes(position) && (
								<time dateTime={m.timestamp} title={fullDateTimeHuman}>
									{shortDateTimeOnlyToday(new Date(m.timestamp))}
								</time>
							)}
							{m.versions.length > 0 && (
								<FontAwesomeIcon
									icon={faPencil}
									title={"Edited " + (m.versions.length - 1) + " times"}
								/>
							)}
							{!m.isIncoming() &&
								m.status === borogove.MessageStatus.MessagePending && (
									<FontAwesomeIcon
										icon={faClock}
										title={m.statusText ?? "Sending..."}
									/>
								)}
							{!m.isIncoming() &&
								m.status === borogove.MessageStatus.MessageFailedToSend && (
									<FontAwesomeIcon
										icon={faCircleExclamation}
										title={m.statusText ?? "Error"}
									/>
								)}
							{!m.isIncoming() &&
								m.status ===
									borogove.MessageStatus.MessageDeliveredToServer && (
									<FontAwesomeIcon
										icon={faCheck}
										title={m.statusText ?? "Sent"}
									/>
								)}
							{!m.isIncoming() &&
								m.status ===
									borogove.MessageStatus.MessageDeliveredToDevice && (
									<FontAwesomeIcon
										icon={faCheckDouble}
										title={m.statusText ?? "Received"}
									/>
								)}
						</div>

						{(chat.getParticipants().length > 2 ||
							m.senderId !== chat.chatId) &&
							["last", "single"].includes(position) &&
							m.isIncoming() && (
								<div className="v-card sender">{sender?.displayName}</div>
							)}
					</div>
				</div>
			</Message.CustomContent>
			{reactionEntries.length > 0 && (
				<Message.Footer className="reactions">
					{reactionEntries.map(([key, reactions]) => (
						<Button
							border
							onClick={() =>
								toggleReaction(
									m,
									reactions.find((r) => r.senderId === accountId) ||
										reactions[0],
								)
							}
							key={key}
							className={
								reactions.find((r) => r.senderId === accountId)
									? "reaction mine"
									: "reaction"
							}
							title={reactions
								.map(
									(r) =>
										chat.getParticipantDetails(r.senderId).displayName +
										" @ " +
										shortDateTimeOnlyToday(new Date(r.timestamp)),
								)
								.join("\n")}
						>
							{renderReaction(reactions[0])} {" " + reactions.length}
						</Button>
					))}
				</Message.Footer>
			)}
		</Message>
	);
};
